import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Container,
  Flex,
  Heading,
  HStack,
  Tag,
  Text,
  Link,
  VStack,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  useColorModeValue,
} from "@chakra-ui/react";
import { SearchIcon, ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { newsItems } from "./NewsList";

function NewsHeroHeader() {
  const accent = "#16A34A";
  const accent2 = "#22C55E";

  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  const titleColor = useColorModeValue("#0F172A", "white");
  const subColor = useColorModeValue("gray.700", "gray.300");

  const heroBg = useColorModeValue(
    "radial-gradient(1000px 420px at 12% 0%, rgba(34,197,94,0.18), transparent 60%), radial-gradient(900px 380px at 88% 25%, rgba(22,163,74,0.14), transparent 60%), linear-gradient(180deg, rgba(2,6,23,0.02), rgba(2,6,23,0.00))",
    "radial-gradient(1000px 420px at 12% 0%, rgba(34,197,94,0.20), transparent 60%), radial-gradient(900px 380px at 88% 25%, rgba(22,163,74,0.14), transparent 60%), linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.00))"
  );

  return (
    <Box pt={{ base: 6, md: 8 }} pb={{ base: 8, md: 10 }} bg={heroBg} borderBottom="1px solid" borderColor={border}>
      <Container maxW="1000px">
        <HStack spacing={2} flexWrap="wrap">
          <Tag size="lg" borderRadius="full" border="1px solid" borderColor={border} fontWeight="800">
            University of Ottawa
          </Tag>

          <Tag size="lg" borderRadius="full" bg="rgba(34,197,94,0.14)" color="#166534" fontWeight="900">
            NEXTCON Lab • SCVIC
          </Tag>
        </HStack>

        <Heading mt={4} fontSize={{ base: "3xl", md: "4xl" }} color={titleColor}>
          <Box as="span" bgClip="text" bgGradient={`linear(to-r, ${accent}, ${accent2})`}>
            News & Announcements
          </Box>
        </Heading>

        <Text mt={3} color={subColor} fontSize={{ base: "md", md: "lg" }} maxW="85ch">
          Latest updates, publications, awards, and milestones from NEXTCON Lab & SCVIC.
        </Text>
      </Container>
    </Box>
  );
}

function NewsCard({ item }) {
  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  const cardBg = useColorModeValue("white", "rgba(255,255,255,0.03)");
  const dateColor = useColorModeValue("#166534", "#86EFAC");

  return (
    <Box
      p={6}
      border="1px solid"
      borderColor={border}
      borderRadius="18px"
      bg={cardBg}
      boxShadow={useColorModeValue("0 10px 24px rgba(0,0,0,0.06)", "none")}
    >
      <Text fontWeight="800" color={dateColor} mb={2}>
        {item.date}
      </Text>

      <Text fontSize="md" color="gray.700">
        {item.text}{" "}
        {item.link && (
          <Link href={item.link} isExternal color="green.600" fontWeight="700">
            View →
          </Link>
        )}
      </Text>
    </Box>
  );
}

function NewsTable() {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const ITEMS_PER_PAGE = 6;

  const pageBg = useColorModeValue("gray.50", "rgba(255,255,255,0.02)");
  const subColor = useColorModeValue("gray.600", "gray.300");

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return newsItems.filter(
      (n) =>
        n.text.toLowerCase().includes(q) ||
        n.date.toLowerCase().includes(q)
    );
  }, [query]);

  useEffect(() => {
    setPage(0);
  }, [query]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const start = page * ITEMS_PER_PAGE;
  const currentItems = filtered.slice(start, start + ITEMS_PER_PAGE);

  return (
    <Box bg={pageBg} minH="100vh">
      <NewsHeroHeader />

      <Container maxW="1000px" py={10}>
        {/* SEARCH */}
        <InputGroup mb={8}>
          <InputLeftElement pointerEvents="none">
            <SearchIcon opacity={0.6} />
          </InputLeftElement>
          <Input
            placeholder="Search news..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            borderRadius="14px"
          />
        </InputGroup>

        {/* NEWS LIST */}
        <VStack spacing={6} align="stretch">
          {currentItems.map((item, index) => (
            <NewsCard key={index} item={item} />
          ))}
        </VStack>

        {/* PAGINATION */}
        {filtered.length > ITEMS_PER_PAGE && (
          <Flex justify="center" align="center" gap={4} mt={10}>
            <IconButton
              icon={<ChevronLeftIcon />}
              aria-label="Previous"
              onClick={() => setPage((p) => Math.max(p - 1, 0))}
              isDisabled={page === 0}
              borderRadius="full"
            />

            <Text fontWeight="600" color={subColor}>
              Page {page + 1} of {totalPages}
            </Text>

            <IconButton
              icon={<ChevronRightIcon />}
              aria-label="Next"
              onClick={() => setPage((p) => Math.min(p + 1, totalPages - 1))}
              isDisabled={page === totalPages - 1}
              borderRadius="full"
            />
          </Flex>
        )}
      </Container>
    </Box>
  );
}

export default NewsTable;